import { useState } from 'react'
import { toast } from 'sonner'

import type {
    CarrierReturnDecisionRequest,
    CarrierShipment,
} from '@/lib/api/carrier-shipment-api'

import { Button } from '@/components/ui/button'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Textarea } from '@/components/ui/textarea'
import { useResolveCarrierReturn } from '@/hooks/use-carrier-shipment-query'
import { ApiError } from '@/lib/api/client'
import { m } from '@/lib/paraglide/messages'

interface Props {
    orderId: string
    shipment: CarrierShipment
    canResolve: boolean
}

export function CarrierReturnActions({ orderId, shipment, canResolve }: Props) {
    const mutation = useResolveCarrierReturn(orderId)
    const [decision, setDecision] =
        useState<CarrierReturnDecisionRequest['decision']>()
    const [note, setNote] = useState('')

    if (shipment.return_decision_state === 'confirmed') {
        return (
            <p className="text-sm">{m.carrier_return_decision_confirmed()}</p>
        )
    }
    if (shipment.return_decision_state === 'reconcile_required') {
        return (
            <p className="text-destructive text-sm">
                {m.carrier_return_decision_reconcile_required()}
            </p>
        )
    }
    if (shipment.return_decision) {
        return <p className="text-sm">{m.carrier_return_decision_pending()}</p>
    }
    if (!canResolve) return null

    const submit = async () => {
        if (!decision) return
        try {
            await mutation.mutateAsync({ decision, note })
            setDecision(undefined)
            setNote('')
        } catch (err) {
            toast.error(
                err instanceof ApiError
                    ? err.message
                    : m.carrier_return_decision_failed(),
            )
        }
    }

    return (
        <>
            <div className="flex flex-wrap gap-2">
                <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setDecision('approve_return')}
                >
                    {m.carrier_return_approve()}
                </Button>
                <Button
                    size="sm"
                    onClick={() => setDecision('request_redelivery')}
                >
                    {m.carrier_return_redeliver()}
                </Button>
            </div>
            <Dialog
                open={decision !== undefined}
                onOpenChange={(open) => {
                    if (!open) {
                        setDecision(undefined)
                        setNote('')
                    }
                }}
            >
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>
                            {m.carrier_return_decision_title()}
                        </DialogTitle>
                        <DialogDescription>
                            {decision === 'approve_return'
                                ? m.carrier_return_approve()
                                : m.carrier_return_redeliver()}
                        </DialogDescription>
                    </DialogHeader>
                    <Textarea
                        maxLength={500}
                        placeholder={m.carrier_return_decision_note()}
                        value={note}
                        onChange={(event) => setNote(event.target.value)}
                    />
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setDecision(undefined)}
                        >
                            {m.common_cancel()}
                        </Button>
                        <Button
                            disabled={mutation.isPending}
                            onClick={() => void submit()}
                        >
                            {m.carrier_return_decision_confirm()}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    )
}
